import {
  getExplorerUrl as sdkGetExplorerUrl,
  getAddressExplorerUrl as sdkGetAddressExplorerUrl,
  getChainById,
} from "@b3dotfun/sdk/shared/utils/chains";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { SOLANA_CHAIN_IDS, SOLANA_DEVNET_CHAIN_ID } from "@/lib/chains";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatAddress(address: string, start = 6, end = 4): string {
  if (!address || address.length < 10) {
    return address;
  }

  return `${address.slice(0, start)}...${address.slice(-end)}`;
}

export function formatUsdValue(
  value: number | null | undefined,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
): string | null {
  if (typeof value !== "number" || Number.isNaN(value) || value <= 0) {
    return null;
  }

  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits,
    maximumFractionDigits,
  })}`;
}

export function formatUsername(username: string | null | undefined): string | null {
  if (!username) {
    return null;
  }
  // Remove .b3.fun suffix if present
  const cleanUsername = username.replace(/\.b3\.fun$/, "");

  // Add @ prefix if not already present
  return cleanUsername.startsWith("@") ? cleanUsername : `@${cleanUsername}`;
}

/**
 * Format a date as a relative time string (e.g., "2 hours ago")
 * @param date - The date to format
 * @returns A human-readable relative time string
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
}

/**
 * Format a duration in milliseconds to a human-readable string
 * @param ms - Duration in milliseconds
 * @returns Formatted duration string (e.g., "1.5s", "2.3m", "1.2h")
 */
export function formatDuration(ms?: number): string {
  if (!ms) return "-";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
  return `${(ms / 3600000).toFixed(1)}h`;
}

/** Get block explorer URL for a transaction by chain ID. Delegates to @b3dotfun/sdk. */
export function getExplorerTxUrl(chainId: number, txHash: string): string {
  if (SOLANA_CHAIN_IDS.has(chainId)) {
    const suffix = chainId === SOLANA_DEVNET_CHAIN_ID ? "?cluster=devnet" : "";
    return `https://solscan.io/tx/${txHash}${suffix}`;
  }
  const chain = getChainById(chainId);
  if (chain) return sdkGetExplorerUrl(chain, txHash);
  return `https://etherscan.io/tx/${txHash}`;
}

/** Get block explorer URL for an address by chain ID. Delegates to @b3dotfun/sdk. */
export function getExplorerAddressUrl(chainId: number, address: string): string {
  if (SOLANA_CHAIN_IDS.has(chainId)) {
    const suffix = chainId === SOLANA_DEVNET_CHAIN_ID ? "?cluster=devnet" : "";
    return `https://solscan.io/account/${address}${suffix}`;
  }
  const chain = getChainById(chainId);
  if (chain) return sdkGetAddressExplorerUrl(chain, address);
  return `https://etherscan.io/address/${address}`;
}

/**
 * Capitalize each word in a string separated by hyphens or spaces
 * @param str - The string to capitalize (e.g., "token-price-mock" or "hello world")
 * @returns Capitalized string (e.g., "Token Price Mock" or "Hello World")
 */
export function capitalizeWords(str: string): string {
  return str
    .split(/[-\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
